import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Baby } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";

const SIZES = ["6x6", "6x9"];
const BINDINGS = ["Saddle Stitch", "Softcover", "Hardcover"];
const FORMATS = ["Physical Copy", "Ebook (Digital Copy)"];
const BOOK_QTYS = [25, 50, 100, 250];
const ART_STYLES = ["Watercolor", "Cartoon", "Hyper-Detailed"];
const PAGES = [8, 10, 12];
const PAGE_PRICES = { 8: 1249, 10: 1599, 12: 1869 };
const QTY_SURCHARGE = { 25: 0, 50: 100, 100: 200, 250: 350 };

const MILESTONES = [
  {
    title: "Character Design & Storyboarding",
    timeline: "1–2 Weeks",
    task: "Develop the visual identity of each character, establish the artistic style, and create storyboards that map out the flow, composition, and storytelling for every page.",
  },
  {
    title: "Rough Sketches (Page Layouts)",
    timeline: "1–2 Weeks",
    task: "Create detailed page layouts and refined sketches, defining character placement, backgrounds, and overall composition before final artwork begins.",
  },
  {
    title: "Coloring & Rendering",
    timeline: "2–4 Weeks",
    task: "Apply vibrant colors, lighting, shadows, textures, and final rendering to bring each illustration to life while ensuring consistency throughout the book.",
  },
];

const selectClass =
  "w-full brutal-border bg-[#F3F4F6] px-4 py-3 font-medium outline-none focus:bg-white";
const labelClass = "block text-xs font-bold uppercase tracking-[0.2em] mb-2";

export default function ChildrensBooks() {
  const [cfg, setCfg] = useState({
    format: "Physical Copy",
    size: "6x6",
    binding: "Saddle Stitch",
    pages: 8,
    bookQty: 25,
    artStyle: "Watercolor",
  });
  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e.target.value }));
  const isPhysical = cfg.format === "Physical Copy";
  let bindingSurcharge = 0;
  if (isPhysical && cfg.binding === "Softcover") bindingSurcharge = 200;
  else if (isPhysical && cfg.binding === "Hardcover") bindingSurcharge = 350;
  const price =
    PAGE_PRICES[cfg.pages] +
    (cfg.size === "6x9" ? 150 : 0) +
    bindingSurcharge +
    (isPhysical ? QTY_SURCHARGE[cfg.bookQty] : 0) +
    (cfg.artStyle === "Hyper-Detailed" ? 300 : 0);

  return (
    <div className="App">
      <Navbar />

      {/* HEADER */}
      <section className="bg-[#1F2530] text-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Link
            to="/"
            data-testid="childrens-back"
            className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-[#F3F4F6]/70 hover:text-[#7BA7D9] transition-colors"
          >
            <ArrowLeft size={16} strokeWidth={2.5} /> Back home
          </Link>
          <span className="block mt-8 text-xs font-bold uppercase tracking-[0.2em] text-[#7BA7D9]">
            Children's Books
          </span>
          <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mt-3">
            Children's Book Designs
          </h1>
          <p className="mt-5 max-w-2xl text-lg font-medium text-[#F3F4F6]/70">
            Bring your children's story to life — choose your size, binding, format and
            page count. Pricing starts at{" "}
            <span className="font-bold text-[#7BA7D9]">$1,249</span>.
          </p>
        </div>
      </section>

      {/* BUILDER */}
      <section className="bg-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div
            data-testid="childrens-milestones"
            className="brutal-border brutal-shadow bg-[#3E6FA8] text-white p-7 sm:p-10 mb-8"
          >
            <h2 className="font-display text-3xl sm:text-4xl">Children's Book Milestones</h2>
            <div className="mt-6 grid sm:grid-cols-3 gap-6">
              {MILESTONES.map((m, i) => (
                <div key={m.title} className="border-l-2 border-white/30 pl-4">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-white/70">
                    Stage {i + 1}
                  </p>
                  <h3 className="font-display text-xl mt-1">{m.title}</h3>
                  <p className="mt-2 text-sm text-white/85">
                    <span className="font-semibold">Task:</span> {m.task}
                  </p>
                  <p className="mt-2 text-sm text-white/85">
                    <span className="font-semibold">Timeline:</span> {m.timeline}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-8">
            {/* CONFIG */}
            <div
              className="lg:col-span-7 brutal-border brutal-shadow bg-white p-7 sm:p-9"
              data-testid="childrens-builder"
            >
              <div className="flex items-center gap-4 mb-7">
                <div className="w-14 h-14 rounded-2xl bg-[#EAF1F8] text-[#3E6FA8] flex items-center justify-center">
                  <Baby size={26} strokeWidth={2} />
                </div>
                <h2 className="font-display text-3xl text-[#1F2530]">Build Your Book</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Format</label>
                  <select data-testid="childrens-format" value={cfg.format} onChange={set("format")} className={selectClass}>
                    {FORMATS.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Book Size</label>
                  <select data-testid="childrens-size" value={cfg.size} onChange={set("size")} className={selectClass}>
                    {SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Art Style</label>
                  <select data-testid="childrens-artstyle" value={cfg.artStyle} onChange={set("artStyle")} className={selectClass}>
                    {ART_STYLES.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </div>

                {isPhysical && (
                  <div>
                    <label className={labelClass}>Binding</label>
                    <select data-testid="childrens-binding" value={cfg.binding} onChange={set("binding")} className={selectClass}>
                      {BINDINGS.map((b) => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className={labelClass}>Pages</label>
                  <select
                    data-testid="childrens-pages"
                    value={cfg.pages}
                    onChange={(e) => setCfg((c) => ({ ...c, pages: Number(e.target.value) }))}
                    className={selectClass}
                  >
                    {PAGES.map((p) => (
                      <option key={p} value={p}>{p} Pages</option>
                    ))}
                  </select>
                </div>

                {isPhysical && (
                  <div className="sm:col-span-2" data-testid="childrens-bookqty-wrap">
                    <label className={labelClass}>Book Quantity</label>
                    <div className="flex flex-wrap gap-2">
                      {BOOK_QTYS.map((q) => (
                        <button
                          key={q}
                          data-testid={`childrens-bookqty-${q}`}
                          onClick={() => setCfg((c) => ({ ...c, bookQty: q }))}
                          className={`min-w-[56px] px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                            cfg.bookQty === q
                              ? "bg-[#3E6FA8] text-white border-[#3E6FA8]"
                              : "bg-white text-[#1F2530] border-[#E3E5E9] hover:border-[#3E6FA8] hover:text-[#3E6FA8]"
                          }`}
                        >
                          {q}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* SUMMARY */}
            <div className="lg:col-span-5">
              <div className="brutal-border brutal-shadow bg-[#1F2530] text-white p-7 sm:p-9 sticky top-24">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#7BA7D9]">
                  Your Children's Book
                </p>
                <dl className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                    <dt className="text-white/60">Format</dt>
                    <dd className="font-semibold text-right">{cfg.format}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                    <dt className="text-white/60">Size</dt>
                    <dd className="font-semibold">{cfg.size}</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                    <dt className="text-white/60">Art Style</dt>
                    <dd className="font-semibold">{cfg.artStyle}</dd>
                  </div>
                  {isPhysical && (
                    <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                      <dt className="text-white/60">Binding</dt>
                      <dd className="font-semibold">{cfg.binding}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                    <dt className="text-white/60">Pages</dt>
                    <dd className="font-semibold">{cfg.pages}</dd>
                  </div>
                  {isPhysical && (
                    <div className="flex justify-between gap-4 border-b border-white/15 pb-2">
                      <dt className="text-white/60">Book Qty</dt>
                      <dd className="font-semibold">{cfg.bookQty}</dd>
                    </div>
                  )}
                </dl>

                <div className="mt-6 flex items-end justify-between">
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-white/60">
                    Total
                  </span>
                  <span
                    data-testid="childrens-price"
                    className="text-base font-bold uppercase tracking-wider bg-[#EAF1F8] text-[#3E6FA8] px-3 py-1.5 rounded-full"
                  >
                    ${price.toLocaleString()}
                  </span>
                </div>

                <Link
                  to="/contact"
                  state={{ service: "Children's Books" }}
                  data-testid="childrens-order"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-6 py-4"
                >
                  Start Your Order <ArrowRight size={20} strokeWidth={2.5} />
                </Link>
                {!isPhysical && (
                  <p className="mt-3 text-xs text-white/60">
                    Ebook (Digital Copy) — book quantity does not apply.
                  </p>
                )}
                <p
                  data-testid="childrens-completion-note"
                  className="mt-4 text-sm font-semibold italic text-red-400"
                >
                  *Please Note: Children's book projects require 30–60 days for
                  completion. Updates are provided after each milestone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
